import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import { listDocumentTypes, updateWorkflowStep } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';
import FieldEditor, { OPTION_TYPES, emptyField } from '../../components/fields/FieldEditor';

export default function WorkflowStepEditModal({ open, onClose, workflowStep }) {
  const queryClient = useQueryClient();
  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes, enabled: open });

  const [checklist, setChecklist] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [fields, setFields] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (workflowStep) {
      setChecklist(workflowStep.checklist || []);
      setRequiredDocs((workflowStep.required_documents || []).map((d) => ({ required: true, verify: true, ...d })));
      setFields(
        (workflowStep.fields || []).map((f) => ({
          ...f,
          options: f.options || [],
          validation: { required: false, date_rule: null, ...(f.validation || {}) },
        }))
      );
      setError('');
    }
  }, [workflowStep]);

  const mutation = useMutation({
    mutationFn: () =>
      updateWorkflowStep(workflowStep.id, {
        checklist: checklist.map((s) => s.trim()).filter(Boolean),
        required_documents: requiredDocs,
        fields,
      }),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Step updated.' });
      queryClient.invalidateQueries({ queryKey: ['workflow-categories'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.errors?.fields?.[0] || err.response?.data?.message || 'Could not update step.'),
  });

  const docTypeName = (code) => typesQuery.data?.find((t) => t.code === code)?.name || code;
  const availableDocTypes = (typesQuery.data || []).filter(
    (t) => t.is_active !== false && !requiredDocs.some((d) => d.code === t.code)
  );

  const addDoc = (code) => {
    if (!code || requiredDocs.some((d) => d.code === code)) return;
    setRequiredDocs((prev) => [...prev, { code, required: true, verify: true }]);
  };

  const removeDoc = (code) => setRequiredDocs((prev) => prev.filter((d) => d.code !== code));

  const setDocOption = (code, patch) => setRequiredDocs((prev) => prev.map((d) => (d.code === code ? { ...d, ...patch } : d)));

  const setChecklistItem = (idx, value) => setChecklist((prev) => prev.map((item, i) => (i === idx ? value : item)));
  const removeChecklistItem = (idx) => setChecklist((prev) => prev.filter((_, i) => i !== idx));
  const addChecklistItem = () => setChecklist((prev) => [...prev, '']);

  const updateField = (id, next) => setFields((prev) => prev.map((f) => (f.id === id ? next : f)));
  const removeField = (id) => setFields((prev) => prev.filter((f) => f.id !== id));
  const addField = () => setFields((prev) => [...prev, emptyField()]);

  const handleSave = () => {
    setError('');
    const blank = fields.find((f) => !f.label.trim());
    if (blank) {
      setError('Every field needs a label.');
      return;
    }
    const badOptions = fields.find((f) => OPTION_TYPES.includes(f.type) && f.options.filter((o) => o.trim()).length < 2);
    if (badOptions) {
      setError(`"${badOptions.label}" needs at least 2 answer options.`);
      return;
    }
    mutation.mutate();
  };

  if (!workflowStep) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit: ${workflowStep.title}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="block text-sm font-medium text-gray-700">Checklist items</p>
            <button type="button" onClick={addChecklistItem} className="text-xs text-primary hover:underline">
              + Add item
            </button>
          </div>
          <div className="space-y-1.5">
            {checklist.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  className="flex-1"
                  placeholder={`Item ${idx + 1}`}
                  value={item}
                  onChange={(e) => setChecklistItem(idx, e.target.value)}
                />
                <button type="button" onClick={() => removeChecklistItem(idx)} className="text-xs text-gray-400 hover:text-red-600">
                  ✕
                </button>
              </div>
            ))}
            {checklist.length === 0 && <p className="text-xs text-gray-400">No checklist items yet.</p>}
          </div>
        </div>
        <div className="border-t border-primary pt-5">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="block text-sm font-medium text-gray-700">Documents</p>
            <Select
              className="w-52"
              value=""
              onChange={(e) => addDoc(e.target.value)}
              disabled={!availableDocTypes.length}
            >
              <option value="">+ Add document…</option>
              {availableDocTypes.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            {requiredDocs.map((entry) => (
              <div key={entry.code} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                <span className="text-sm text-gray-700">{docTypeName(entry.code)}</span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    <input
                      type="checkbox"
                      className="accent-primary"
                      checked={entry.required}
                      onChange={(e) => setDocOption(entry.code, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  {entry.required && (
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        className="accent-primary"
                        checked={entry.verify}
                        onChange={(e) => setDocOption(entry.code, { verify: e.target.checked })}
                      />
                      Must be verified
                    </label>
                  )}
                  <button type="button" onClick={() => removeDoc(entry.code)} className="text-xs text-gray-400 hover:text-red-600">
                    ✕
                  </button>
                </div>
              </div>
            ))}
            {requiredDocs.length === 0 && <p className="text-xs text-gray-400">No documents yet.</p>}
          </div>
        </div>

        <div className="border-t border-primary pt-5">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="block text-sm font-medium text-gray-700">Fields</p>
            <button type="button" onClick={addField} className="text-xs text-primary hover:underline">
              + Add field
            </button>
          </div>
          <div className="space-y-2">
            {fields.map((f) => (
              <FieldEditor key={f.id} field={f} onChange={(next) => updateField(f.id, next)} onRemove={() => removeField(f.id)} />
            ))}
            {fields.length === 0 && <p className="text-xs text-gray-400">No fields yet.</p>}
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
